import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ItemPicker, type ExtendedItemProps} from '@github-ui/item-picker-base/ItemPicker'
import {useCallback, useMemo, useRef, useState, type RefObject} from 'react'
import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {createIssueTypePickerItemLeadingVisual, type IssueTypeColor} from './IssueTypePickerItemLeadingVisual'
import {SPECIAL_VALUES} from '@github-ui/item-picker-base/constants/placeholders'

export type IssueTypePickerIssueType = {
  id: string
  name: string
  description: string | null | undefined
  color: IssueTypeColor
  isEnabled?: boolean
}

export type IssueTypePickerProps<TIssueType extends IssueTypePickerIssueType = IssueTypePickerIssueType> = {
  title?: string
  width?: 'small' | 'medium' | 'large'
  issueId?: string
  owner: string
  repo: string
  activeIssueType: TIssueType | null
  showUnset?: boolean
  noTypeItem?: ExtendedItemProps<TIssueType>
  readonly: boolean
  insidePortal?: boolean
  triggerOpen?: boolean
  shortcutEnabled: boolean
  issueTypeToken?: string
  anchorElement: (props: React.HTMLAttributes<HTMLElement>, ref?: RefObject<HTMLButtonElement>) => React.ReactElement
  onSelectionChange: (issueType: TIssueType[]) => void
  onIssueUpdate?: () => void
  /**
   * Whether to render the issue type picker as a nested select panel (true) versus a standalone select
   * panel (false; default).
   */
  nested?: boolean
}

type ItemPickerWrapperProps<TIssueType extends IssueTypePickerIssueType = IssueTypePickerIssueType> =
  IssueTypePickerProps<TIssueType> & {
    fetchedIssueTypes: TIssueType[]
    isLoading: boolean
  }

export const UNSET_ID = 'UNSET'

export function IssueTypePickerBase<TIssueType extends IssueTypePickerIssueType = IssueTypePickerIssueType>({
  title = LABELS.typesHeader,
  width = 'large',
  triggerOpen = true,
  fetchedIssueTypes,
  activeIssueType,
  showUnset = false,
  onSelectionChange,
  insidePortal,
  anchorElement,
  isLoading,
  nested,
  issueTypeToken,
  noTypeItem,
}: ItemPickerWrapperProps<TIssueType>) {
  const [filter, setFilter] = useState('')

  const issueTypeFromToken = useMemo(() => {
    // For types containing spaces, the type token will be surrounded by double quotes, but the types from the picker will be without quotes
    const formattedIssueTypeToken =
      issueTypeToken && issueTypeToken[0] === '"' && issueTypeToken[issueTypeToken.length - 1] === '"'
        ? issueTypeToken.slice(1, -1)
        : issueTypeToken

    return fetchedIssueTypes.find(issueType => issueType.name === formattedIssueTypeToken)
  }, [fetchedIssueTypes, issueTypeToken])

  const items = useMemo(() => {
    const repositoryIssueTypes = fetchedIssueTypes.slice().sort((a, b) => a.name.localeCompare(b.name))
    if (noTypeItem) {
      repositoryIssueTypes.unshift(SPECIAL_VALUES.noTypeData as TIssueType)
    } else if (showUnset) {
      repositoryIssueTypes.unshift({
        id: UNSET_ID,
        name: 'No issue type',
        isEnabled: true,
        description: '',
        color: 'GRAY',
      } as TIssueType)
    }
    if (!filter) return repositoryIssueTypes
    return repositoryIssueTypes.filter(l => l.name.toLowerCase().indexOf(filter.toLowerCase()) >= 0)
  }, [fetchedIssueTypes, filter, showUnset, noTypeItem])

  const filterItems = useCallback((value: string) => {
    setFilter(value)
  }, [])

  const getItemKey = useCallback((issueType: TIssueType) => issueType.id, [])

  const convertToItemProps = useCallback((issueType: TIssueType): ExtendedItemProps<TIssueType> => {
    return {
      id: issueType.id,
      text: issueType.name,
      description: issueType.description ?? '',
      descriptionVariant: 'block',
      leadingVisual: createIssueTypePickerItemLeadingVisual(issueType.color),
      source: issueType,
    }
  }, [])

  const initialSelectedItems = useMemo(() => {
    if (noTypeItem?.selected) {
      return [SPECIAL_VALUES.noTypeData as TIssueType]
    }
    if (activeIssueType) return [activeIssueType]
    if (issueTypeFromToken) return [issueTypeFromToken]
    return []
  }, [activeIssueType, issueTypeFromToken, noTypeItem])

  const anchorRef = useRef<HTMLButtonElement>(null)
  const showNoMatchMessage = useMemo(() => items.length === 0, [items.length])

  const responsiveOnNarrowEnabled = isFeatureEnabled('issues_react_select_panel_fullscreen_on_narrow')

  return (
    <ItemPicker
      loading={isLoading}
      items={items}
      initialSelectedItems={initialSelectedItems}
      filterItems={filterItems}
      title={title}
      getItemKey={getItemKey}
      convertToItemProps={convertToItemProps}
      placeholderText={`Filter types`}
      selectionVariant="single"
      onSelectionChange={onSelectionChange}
      renderAnchor={anchorProps => anchorElement(anchorProps)}
      insidePortal={insidePortal}
      height={'large'}
      width={width}
      nested={nested}
      resultListAriaLabel={'Issue Type results'}
      triggerOpen={triggerOpen}
      keybindingCommandId="item-pickers:open-issue-type"
      selectPanelRef={anchorRef}
      improvedNoMatchAccessibility={showNoMatchMessage}
      noMatchMessage={
        showNoMatchMessage
          ? {
              title: 'No issue types were found',
              variant: 'empty',
              body: 'Try searching with a different query for results.',
            }
          : undefined
      }
      responsiveOnNarrow={responsiveOnNarrowEnabled}
    />
  )
}

try{ IssueTypePickerBase.displayName ||= 'IssueTypePickerBase' } catch {}